import { MatchKeysAndValues, MatchKeysAndValuesProjection, Pagination } from "../../../@components/interfaces";
import { IMyApps } from "../../../jobs/models/my-apps-resp";
import { xget, xhttp } from "../Fetchv2";
import { User } from "../responses/users_uid_posts_pid_comments.js";
import { receipt, subscriptions } from "../schemas/mg.schema";
import { PGST } from "../schemas/pg.schema-types.js";
import { PGSchema } from "../schemas/pg.schema.js";
import { SR } from "../schemas/schemas/schema-reponses.js";

class Billing {
	// ================== PAYMENTS ==================
	async getReceipt(co_id: string, pagination?: Pagination) {
		let data = await xget<receipt[]>(`/pages/${co_id}/receipt`, pagination);
		return data;
	}
	async getMySub(co_id: string, pagination?: Pagination) {
		let data = await xget<{ sub: subscriptions[]; used: { sid: number; used: number }[] }>(`/pages/${co_id}/subscriptions`, pagination);
		return data;
	}

	async getInvoices(co_id: string, page: number, token: string) {
		return await xget<PGSchema.invoice[]>(`/pages/${co_id}/invoices`, { page }, { headers: { "app-token": token } });
	}

	async getPaymentMethods(page_id: string, token: string) {
		// TODO: change any[] to the correct type
		return await xget<any[]>(`/pages/${page_id}/payment_methods`, {}, { headers: { "app-token": token } });
	}

	async savePaymentMethod(page_id: string, token: string, data: { cardNumber: string; cardHolder: string; expMonth: string; expYear: string; cvc: string }) {
		return await xhttp(`/pages/${page_id}/payment_methods`, "POST", data, { headers: { "app-token": token } });
	}
}

class Jobs {
	async repostJob(co_id: string, jid: number, token?: string) {
		let data = await xhttp<{ id: number }>(`/pages/${co_id}/jobs/${jid}/repost`, "POST", { token });
		return data;
	}
	async duplicateJob(
		co_id: string,
		jid: number,
		data: {
			status?: PGST.jobs.status;
			vacancies?: number;
			branch?: string;
			branches?: PGSchema.jobs["branches"];
			hide_company?: PGSchema.jobs["hide_company"];
			internal_id?: string;
			request_reason_id?: PGSchema.jobs["request_reason_id"];
			request_employee_id?: PGSchema.jobs["request_employee_id"];
			request_employee_name?: PGSchema.jobs["request_employee_name"];
		},
		token: string
	) {
		return xhttp<{ id: number }>(`/pages/${co_id}/jobs/${jid}/duplicate`, "POST", data, { headers: { "app-token": token } });
	}

	async createRequisition(
		co_id: string,
		jid: number,
		data: {
			status?: PGST.jobs.status;
			vacancies?: number;
			entity?: PGSchema.jobs["entity"];
			branches?: PGSchema.jobs["branches"];
			hide_company?: PGSchema.jobs["hide_company"];
			internal_id?: string;
			request_reason_id?: PGSchema.jobs["request_reason_id"];
			request_employee_id?: PGSchema.jobs["request_employee_id"];
			request_employee_name?: PGSchema.jobs["request_employee_name"];
			request_comments?: PGSchema.jobs["request_comments"];
		},
		token: string
	) {
		return xhttp<{ id: number }>(`/pages/${co_id}/jobs/${jid}/requisition`, "POST", data, { headers: { "app-token": token } });
	}

	async deleteJob(co_id: PGSchema.jobs["co_id"], job_id: PGSchema.jobs["job_id"], token: string) {
		let data = await xhttp(`/pages/${co_id}/jobs/${job_id}`, "DELETE", { token });
		return data;
	}
}

class Candidates {
	async getStats(pgid: string, jid: number, module_: string) {
		return xget<SR.apiv2.pages.candidates.getStats>(`/pages/${pgid}/${module_}/${jid}/stats`);
	}

	async reindexByJob(pgid: PGSchema.pages["id"], jid: PGSchema.jobs["job_id"], module_: string, uids: string[], token: string) {
		return xget<SR.apiv2.pages.candidates.getStats>(`/pages/${pgid}/${module_}/${jid}/reindex`, { uids: uids.join(",") }, { headers: { "app-token": token } });
	}
	async reindex(pgid: PGSchema.pages["id"], token: string) {
		return xget<SR.apiv2.pages.candidates.getStats>(`/pages/${pgid}/reindex`, {}, { headers: { "app-token": token } });
	}
}

class Branches {
	async getAll(pgid: string, token: string) {
		return xget<PGSchema.branches[]>(`/pages/${pgid}/branches`, undefined, { headers: { "app-token": token } });
	}

	async getOne(pgid: string, branchId: string, token: string) {
		return xget<PGSchema.branches>(`/pages/${pgid}/branches/${branchId}`, undefined, { headers: { "app-token": token } });
	}

	async create(pgid: string, data: Omit<PGSchema.branches, "id" | "created_at" | "updated_at" | "pgid">, token: string) {
		return xhttp<PGSchema.branches>(`/pages/${pgid}/branches`, "POST", data, { headers: { "app-token": token } });
	}

	async update(pgid: string, branchId: string, data: Partial<Omit<PGSchema.branches, "id" | "created_at" | "updated_at" | "pgid">>, token: string) {
		return xhttp<PGSchema.branches>(`/pages/${pgid}/branches/${branchId}`, "PUT", data, { headers: { "app-token": token } });
	}

	async delete(pgid: string, branchId: string, token: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/branches/${branchId}`, "DELETE", {}, { headers: { "app-token": token } });
	}
}

class UsersBlock {
	async block(pgid: string, uid: string, token: string) {
		return xhttp<{ blocked: PGSchema.users_jobs_block; user: Partial<PGSchema.users> }>(`/pages/${pgid}/users/block`, "POST", { uid }, { headers: { "app-token": token } });
	}

	async list(pgid: string, token: string) {
		return xget<{ blocked: PGSchema.users_jobs_block[]; users: Partial<PGSchema.users>[] }>(`/pages/${pgid}/users/block`, undefined, { headers: { "app-token": token } });
	}

	async unblock(pgid: string, uid: string, token: string) {
		return xhttp<{ unblocked: boolean }>(
			`/pages/${pgid}/users/block`,
			"DELETE",
			{
				uid
			},
			{ headers: { "app-token": token } }
		);
	}

	async search(pgid: string, email: string, token: string) {
		return xget<{ user: PGSchema.users; blocked: boolean }>(`/pages/${pgid}/users/block/search`, { email }, { headers: { "app-token": token } });
	}
}

class RequestReasons {
	async getAll(pgid: string, token: string) {
		return xget<PGSchema.request_reasons[]>(`/pages/${pgid}/request_reasons`, undefined, { headers: { "app-token": token } });
	}

	async getOne(pgid: string, itemId: string, token: string) {
		return xget<PGSchema.request_reasons>(`/pages/${pgid}/request_reasons/${itemId}`, undefined, { headers: { "app-token": token } });
	}

	async create(pgid: string, data: Omit<PGSchema.request_reasons, "id" | "created_at" | "updated_at" | "pgid">, token: string) {
		return xhttp<PGSchema.request_reasons>(`/pages/${pgid}/request_reasons`, "POST", data, { headers: { "app-token": token } });
	}

	async update(pgid: string, itemId: string, data: Partial<Omit<PGSchema.request_reasons, "id" | "created_at" | "updated_at" | "pgid">>, token: string) {
		return xhttp<PGSchema.request_reasons>(`/pages/${pgid}/request_reasons/${itemId}`, "PUT", data, { headers: { "app-token": token } });
	}

	async delete(pgid: string, itemId: string, token: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/request_reasons/${itemId}`, "DELETE", {}, { headers: { "app-token": token } });
	}
}

class ExtProviders {
	async getAll(pgid: string, token: string, type?: string) {
		return xget<PGSchema.ext_providers[]>(`/pages/${pgid}/ext-providers`, type ? { type } : undefined, { headers: { "app-token": token } });
	}

	async getOne(pgid: string, providerId: string, token: string) {
		return xget<PGSchema.ext_providers>(`/pages/${pgid}/ext-providers/${providerId}`, undefined, { headers: { "app-token": token } });
	}

	async create(pgid: string, data: Omit<PGSchema.ext_providers, "id" | "created_at" | "updated_at" | "pgid">, token: string) {
		return xhttp<PGSchema.ext_providers>(`/pages/${pgid}/ext-providers`, "POST", data, { headers: { "app-token": token } });
	}

	async update(pgid: string, providerId: string, data: Partial<Omit<PGSchema.ext_providers, "id" | "created_at" | "updated_at" | "pgid">>, token: string) {
		return xhttp<PGSchema.ext_providers>(`/pages/${pgid}/ext-providers/${providerId}`, "PUT", data, { headers: { "app-token": token } });
	}

	async delete(pgid: string, providerId: string, token: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/ext-providers/${providerId}`, "DELETE", {}, { headers: { "app-token": token } });
	}
}

class LegalEntities {
	async getAll(pgid: string, token: string) {
		return xget<PGSchema.hcm_entities[]>(`/pages/${pgid}/entities`, undefined, { headers: { "app-token": token } });
	}

	async getOne(pgid: string, itemId: string, token: string) {
		return xget<PGSchema.hcm_entities>(`/pages/${pgid}/entities/${itemId}`, undefined, { headers: { "app-token": token } });
	}

	async create(pgid: string, data: Omit<PGSchema.hcm_entities, "id" | "created_at" | "updated_at" | "page_id" | "psm_page">, token: string) {
		return xhttp<PGSchema.hcm_entities>(`/pages/${pgid}/entities`, "POST", data, { headers: { "app-token": token } });
	}

	async update(pgid: string, itemId: string, data: Partial<Omit<PGSchema.hcm_entities, "id" | "created_at" | "updated_at" | "page_id" | "psm_page">>, token: string) {
		return xhttp<PGSchema.hcm_entities>(`/pages/${pgid}/entities/${itemId}`, "PUT", data, { headers: { "app-token": token } });
	}

	async delete(pgid: string, itemId: string, token: string) {
		return xhttp<{ deleted: boolean }>(`/pages/${pgid}/entities/${itemId}`, "DELETE", {}, { headers: { "app-token": token } });
	}
}

export class Pages {
	billing = new Billing();
	jobs = new Jobs();
	candidates = new Candidates();
	branches = new Branches();
	usersBlock = new UsersBlock();
	requestReasons = new RequestReasons();
	extProviders = new ExtProviders();
	entities = new LegalEntities();

	getEmailProvider(domain: string) {
		return xhttp<{ email_provider: "error" | "google" | "microsoft" | "other" }>(`/email_provider`, "POST", { domain });
	}

	// ================== PAGES ==================
	getPage(_id: string, appToken?: string) {
		return xget<PGSchema.pages>(`/pages/${_id}`, undefined, appToken ? { headers: { "app-token": appToken } } : undefined);
	}

	async createPage(queryParams?: FormData) {
		let response = await xhttp<{ created: boolean; id: string }>(`/pages`, "POST", queryParams);
		window.app.pages.clear();
		return response;
	}

	async checkusername(username?: string) {
		return xhttp<{ _id: string }>(`/pages/username`, "SEARCH", { u: username });
	}
	async generateCandidateTokenView(pgid: string, uid: string, token: string) {
		return xhttp<{ token: string }>(`/pages/${pgid}/user_token`, "POST", { uid }, { headers: { "app-token": token } });
	}

	async pushwebhook(uid: string, test_id: string, token?: string) {
		let r = await xhttp<{ http: { headers: any; body: string; status: number } }>(`/pages/${uid}/pushwebhook`, "POST", { test_id }, { headers: { "app-token": token! } });
		return r;
	}

	async webhookLogs(uid: string, webhook_id: PGSchema.webhooks["id"], token?: string) {
		return await xget<{ logs: PGSchema.webhooks_logs[] }>(`/pages/${uid}/webhooks/${webhook_id}/logs`, undefined, { headers: { "app-token": token! } });
	}

	async resendWebhook(uid: string, webhook_id: PGSchema.webhooks["id"], log_id: PGSchema.webhooks_logs["id"], token?: string) {
		return await xhttp<{ log: PGSchema.webhooks_logs }>(`/pages/${uid}/webhooks/${webhook_id}/logs/${log_id}`, "POST", {}, { headers: { "app-token": token! } });
	}

	// ================== API KEYS (OAuth2 Client Credentials) ==================
	async listApiKeys(uid: string, token: string) {
		return await xget<{ apikeys: PGSchema.webservices_api[] }>(`/pages/${uid}/apikeys`, undefined, { headers: { "app-token": token } });
	}

	async createApiKey(uid: string, data: { name?: string; scopes?: PGST.webservices_api.scopes[] }, token: string) {
		return await xhttp<{ apikey: PGSchema.webservices_api }>(`/pages/${uid}/apikeys`, "POST", data, { headers: { "app-token": token } });
	}

	async updateApiKey(uid: string, id: PGSchema.webservices_api["id"], data: { name?: string; scopes?: PGST.webservices_api.scopes[]; status?: PGST.webservices_api.status }, token: string) {
		return await xhttp<{ apikey: PGSchema.webservices_api }>(`/pages/${uid}/apikeys/${id}`, "PUT", data, { headers: { "app-token": token } });
	}

	async deleteApiKey(uid: string, id: PGSchema.webservices_api["id"], token: string) {
		return await xhttp<{ deleted: boolean }>(`/pages/${uid}/apikeys/${id}`, "DELETE", {}, { headers: { "app-token": token } });
	}

	async getPageConfig(uid: string, cols?: MatchKeysAndValuesProjection<PGSchema.pages>, token?: string) {
		let r = await xget<PGSchema.pages>(`/pages/${uid}/config`, cols ? { cols: JSON.stringify(cols) } : undefined, { headers: { "app-token": token! } });
		return r;
	}

	async updatePageConfig(uid: string, data: Partial<PGSchema.pages>, token: string) {
		return await xhttp<{ updated: boolean; page: PGSchema.pages }>(`/pages/${uid}/config`, "PUT", data, { headers: { "app-token": token } });
	}

	async deletePageConfig(uid: string, data: Partial<PGSchema.pages>, token: string) {
		return await xhttp(`/pages/${uid}/config`, "DELETE", data, { headers: { "app-token": token } });
	}

	async editPagePP(uid: string, form: FormData, token: string) {
		let res = await xhttp<{ pp: string; thumb_pp: string; cover: string }>(`/pages/${uid}/pp`, "PUT", form, { headers: { "app-token": token } });
		return res;
	}

	//#region ------------------- ADMINS -------------------

	async getAdmins(pgid: string, token: string) {
		return xget<(PGSchema.users_pages & Pick<PGSchema.users, "id" | "name" | "last_name" | "username" | "thumb_pp" | "is_verified" | "email" | "insignia" | "user_lang">)[]>(
			`/pages/${pgid}/admins`,
			undefined,
			{ headers: { "app-token": token } }
		);
	}

	async getAdminsv2(pgid: string, token: string, params?: { timestamp?: number }) {
		return xget<{ users_pages: PGSchema.users_pages[]; faces: { uid: string }[] }>(`/pages/${pgid}/adminsv2`, params ? { ...params } : undefined, { headers: { "app-token": token } });
	}

	async addAdmin(pgid: string, body: Partial<Pick<PGSchema.users_pages, "rol" | "permissions" | "status">> & Pick<PGSchema.users_pages, "uid">, token: string) {
		let data = await xhttp<{
			user_page: PGSchema.users_pages;
			user: Pick<PGSchema.users, "id" | "name" | "last_name" | "username" | "thumb_pp" | "is_verified" | "email" | "insignia" | "user_lang">;
		}>(`/pages/${pgid}/admins`, "POST", { users_pages: body }, { headers: { "app-token": token! } });
		window.app.pages.teams = {};
		window.app.pages.clear();
		return data;
	}

	async editAdmin(pgid: PGSchema.pages["id"], uid: PGSchema.users["id"], body: Partial<PGSchema.users_pages>, token: string) {
		let data = await xhttp<{ updated: boolean; user: User }>(`/pages/${pgid}/admins/${uid}`, "PUT", { user: body }, { headers: { "app-token": token } });
		window.app.pages.clear();
		return data;
	}

	async deleteAdmin(pgid: PGSchema.pages["id"], uid: PGSchema.users["id"], token: string) {
		let data = await xhttp(`/pages/${pgid}/admins/${uid}`, "DELETE", {}, { headers: { "app-token": token } });
		window.app.pages.clear();
		return data;
	}

	async revokeAdmin(pgid: PGSchema.pages["id"], uid: PGSchema.users["id"], token: string) {
		let data = await xhttp(`/pages/${pgid}/admins/${uid}/revoke`, "DELETE", {}, { headers: { "app-token": token } });
		window.app.pages.clear();
		return data;
	}

	//#region ------------------- JOBS -------------------
	async getJob(co_id: PGSchema.jobs["co_id"], job_id: PGSchema.jobs["job_id"], token?: string) {
		return xget<
			{ job: PGSchema.jobs; applied: boolean; branches: PGSchema.branches[] },
			{
				type: "JobIsPrivateException";
				domains: string[];
				job: Pick<PGSchema.jobs, "job_id" | "co_id" | "title" | "type" | "country" | "state" | "city"> & {
					psm_page: Pick<PGSchema.pages, "name" | "username" | "thumb_pp" | "is_verified" | "cover">;
				};
			}
		>(`/pages/${co_id}/jobs2/${job_id}`, {}, { headers: token ? { "app-token": token } : {} });
	}

	async postAJob(co_id: string, job: PGSchema.jobs, ai: { interviews: boolean; tests: boolean }, token: string) {
		let data = await xhttp<{ id: number; job: PGSchema.jobs; business_domain: string }>(`/pages/${co_id}/jobs`, "POST", { job: job, ai }, { headers: { "app-token": token } });
		return data;
	}

	async editAJob(job: MatchKeysAndValues<PGSchema.jobs>, token: string) {
		return xhttp(`/pages/${job.co_id}/jobs/${job.job_id}`, "PUT", { ...job }, { headers: { "app-token": token } });
	}

	async domainPages() {
		return xget<PGSchema.page_domains[]>(`/pages/domain`);
	}

	// apps
	async getMyApps(pag?: Pagination) {
		let response = await xget<IMyApps>(`/users/me/apps`, pag);
		return response;
	}

	async checkDomain(body: { domain: string }) {
		let response = await xhttp<IMyApps>(`/pages/checkdomain`, "POST", body);
		return response;
	}

	async autoJoin(
		pgid: string,
		data: {
			permissions: PGST.users_pages.permissions[];
		}
	) {
		return await xhttp<PGSchema.users_pages>(`/pages/${pgid}/join`, "POST", data);
	}

	async cancelJoinRequest(uid: string) {
		return await xhttp<boolean>(`/pages/${uid}/join`, "DELETE");
	}

	async getPageLogs(pgid: string, token?: string) {
		return await xget<{ logs: PGSchema.page_logs[] }>(`/pages/${pgid}/logs`, undefined, { headers: { "app-token": token! } });
	}
}
